import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import BookIcon from '@mui/icons-material/Book';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SchoolIcon from '@mui/icons-material/School';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import QuizIcon from '@mui/icons-material/Quiz';
import { getImagePath } from '../utils/imageUtils';

const resources = {
  'Mathematics Olympiad': {
    studyMaterials: [
      {
        title: 'IMO Foundation Guide',
        description: 'Comprehensive guide covering basic to advanced concepts for IMO preparation',
        type: 'PDF',
        size: '15MB',
        downloadUrl: '/resources/math/imo-foundation.pdf',
        image: getImagePath('study-material.jpg'),
      },
      {
        title: 'Number Theory Workbook',
        description: 'Practice problems and solutions for number theory topics',
        type: 'PDF',
        size: '8MB',
        downloadUrl: '/resources/math/number-theory.pdf',
        image: getImagePath('study-material.jpg'),
      },
      {
        title: 'Geometry Problem Set',
        description: 'Collection of geometry problems with detailed solutions',
        type: 'PDF',
        size: '12MB',
        downloadUrl: '/resources/math/geometry.pdf',
        image: getImagePath('study-material.jpg'),
      },
    ],
    videoLectures: [
      {
        title: 'Introduction to Olympiad Mathematics',
        duration: '45 mins',
        instructor: 'Dr. Rajesh Kumar',
        thumbnail: getImagePath('video-lecture.jpg'),
        videoUrl: '/resources/math/video1.mp4',
      },
      {
        title: 'Problem Solving Techniques',
        duration: '60 mins',
        instructor: 'Dr. Rajesh Kumar',
        thumbnail: getImagePath('video-lecture.jpg'),
        videoUrl: '/resources/math/video2.mp4',
      },
    ],
    practiceTests: [
      {
        title: 'IMO Level 1 Mock Test',
        duration: '3 hours',
        questions: 30,
        downloadUrl: '/resources/math/mock1.pdf',
        image: getImagePath('practice-test.jpg'),
      },
      {
        title: 'IMO Level 2 Practice Test',
        duration: '4 hours',
        questions: 40,
        downloadUrl: '/resources/math/mock2.pdf',
        image: getImagePath('practice-test.jpg'),
      },
    ],
  },
  'Science Olympiad': {
    studyMaterials: [
      {
        title: 'NSO Complete Guide',
        description: 'Comprehensive guide for NSO preparation covering Physics, Chemistry, and Biology',
        type: 'PDF',
        size: '20MB',
        downloadUrl: '/resources/science/nso-guide.pdf',
        image: getImagePath('study-material.jpg'),
      },
      {
        title: 'Physics Olympiad Workbook',
        description: 'Practice problems and solutions for physics topics',
        type: 'PDF',
        size: '10MB',
        downloadUrl: '/resources/science/physics.pdf',
        image: getImagePath('study-material.jpg'),
      },
      {
        title: 'Chemistry Problem Set',
        description: 'Collection of chemistry problems with detailed solutions',
        type: 'PDF',
        size: '8MB',
        downloadUrl: '/resources/science/chemistry.pdf',
        image: getImagePath('study-material.jpg'),
      },
    ],
    videoLectures: [
      {
        title: 'Introduction to Olympiad Science',
        duration: '50 mins',
        instructor: 'Dr. Priya Sharma',
        thumbnail: getImagePath('video-lecture.jpg'),
        videoUrl: '/resources/science/video1.mp4',
      },
      {
        title: 'Scientific Reasoning Methods',
        duration: '55 mins',
        instructor: 'Dr. Priya Sharma',
        thumbnail: getImagePath('video-lecture.jpg'),
        videoUrl: '/resources/science/video2.mp4',
      },
    ],
    practiceTests: [
      {
        title: 'NSO Level 1 Mock Test',
        duration: '3 hours',
        questions: 35,
        downloadUrl: '/resources/science/mock1.pdf',
        image: getImagePath('practice-test.jpg'),
      },
      {
        title: 'NSO Level 2 Practice Test',
        duration: '4 hours',
        questions: 45,
        downloadUrl: '/resources/science/mock2.pdf',
        image: getImagePath('practice-test.jpg'),
      },
    ],
  },
  'English Olympiad': {
    studyMaterials: [
      {
        title: 'IEO Complete Guide',
        description: 'Comprehensive guide for IEO preparation covering grammar, vocabulary, and comprehension',
        type: 'PDF',
        size: '12MB',
        downloadUrl: '/resources/english/ieo-guide.pdf',
        image: getImagePath('study-material.jpg'),
      },
      {
        title: 'Advanced Grammar Workbook',
        description: 'Practice exercises for advanced grammar concepts',
        type: 'PDF',
        size: '6MB',
        downloadUrl: '/resources/english/grammar.pdf',
        image: getImagePath('study-material.jpg'),
      },
      {
        title: 'Vocabulary Builder',
        description: 'Essential vocabulary for IEO preparation',
        type: 'PDF',
        size: '4MB',
        downloadUrl: '/resources/english/vocabulary.pdf',
        image: getImagePath('study-material.jpg'),
      },
    ],
    videoLectures: [
      {
        title: 'Introduction to Olympiad English',
        duration: '40 mins',
        instructor: 'Prof. Amit Patel',
        thumbnail: getImagePath('video-lecture.jpg'),
        videoUrl: '/resources/english/video1.mp4',
      },
      {
        title: 'Advanced Writing Techniques',
        duration: '50 mins',
        instructor: 'Prof. Amit Patel',
        thumbnail: getImagePath('video-lecture.jpg'),
        videoUrl: '/resources/english/video2.mp4',
      },
    ],
    practiceTests: [
      {
        title: 'IEO Level 1 Mock Test',
        duration: '2.5 hours',
        questions: 40,
        downloadUrl: '/resources/english/mock1.pdf',
        image: getImagePath('practice-test.jpg'),
      },
      {
        title: 'IEO Level 2 Practice Test',
        duration: '3 hours',
        questions: 50,
        downloadUrl: '/resources/english/mock2.pdf',
        image: getImagePath('practice-test.jpg'),
      },
    ],
  },
};

const Resources = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedResourceType, setSelectedResourceType] = useState('studyMaterials');
  const theme = useTheme();

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleResourceTypeChange = (event, newValue) => {
    setSelectedResourceType(newValue);
  };

  const getTabLabel = (index) => {
    const labels = ['Mathematics Olympiad', 'Science Olympiad', 'English Olympiad'];
    return labels[index];
  };

  const getResourceTypeLabel = (type) => {
    const labels = {
      studyMaterials: 'Study Materials',
      videoLectures: 'Video Lectures',
      practiceTests: 'Practice Tests',
    };
    return labels[type];
  };

  const renderResourceContent = () => {
    const currentResources = resources[getTabLabel(selectedTab)][selectedResourceType];

    switch (selectedResourceType) {
      case 'studyMaterials':
        return (
          <Grid container spacing={3}>
            {currentResources.map((material, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <BookIcon sx={{ color: 'primary.main', mr: 1 }} />
                      <Typography variant="h6">{material.title}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {material.description}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        {material.type} • {material.size}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DownloadIcon />}
                        href={material.downloadUrl}
                      >
                        Download
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        );

      case 'videoLectures':
        return (
          <Grid container spacing={3}>
            {currentResources.map((video, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card>
                  <CardMedia
                    component="img"
                    height="200"
                    image={video.thumbnail}
                    alt={video.title}
                  />
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {video.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Duration: {video.duration} • Instructor: {video.instructor}
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<VideoLibraryIcon />}
                      href={video.videoUrl}
                    >
                      Watch Now
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        );

      case 'practiceTests':
        return (
          <Grid container spacing={3}>
            {currentResources.map((test, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <QuizIcon sx={{ color: 'primary.main', mr: 1 }} />
                      <Typography variant="h6">{test.title}</Typography>
                    </Box>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <AccessTimeIcon />
                        </ListItemIcon>
                        <ListItemText primary="Duration" secondary={test.duration} />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <EmojiEventsIcon />
                        </ListItemIcon>
                        <ListItemText primary="Questions" secondary={test.questions} />
                      </ListItem>
                    </List>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<DownloadIcon />}
                      href={test.downloadUrl}
                    >
                      Download Test
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Typography variant="h3" component="h1" gutterBottom align="center">
        Olympiad Resources
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" align="center" paragraph>
        Access comprehensive study materials, video lectures, and practice tests for Olympiad preparation
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          centered
          variant="fullWidth"
          sx={{ mb: 2 }}
        >
          {['Mathematics Olympiad', 'Science Olympiad', 'English Olympiad'].map((label, index) => (
            <Tab key={label} label={label} />
          ))}
        </Tabs>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
        <Tabs
          value={selectedResourceType}
          onChange={handleResourceTypeChange}
          centered
          variant="fullWidth"
          sx={{ mb: 2 }}
        >
          {['studyMaterials', 'videoLectures', 'practiceTests'].map((type) => (
            <Tab key={type} label={getResourceTypeLabel(type)} value={type} />
          ))}
        </Tabs>
      </Box>

      {renderResourceContent()}
    </Container>
  );
};

export default Resources; 